/**
 * TeamManagement Component
 *
 * Full team management interface with seat usage, member list,
 * pending invites, and invite form.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  TextInput,
} from 'react-native';
import {
  Users,
  UserPlus,
  Crown,
  Shield,
  User,
  Mail,
  AlertCircle,
  Check,
  ChevronDown,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInUp, Layout } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  useTeamStore,
  useActiveMembers,
  usePendingInvites,
  useUsedSeats,
  useMyRole,
  canManageTeam,
  getTotalSeats,
  ROLE_NAMES,
  ADDITIONAL_SEATS_BY_TIER,
} from '@/lib/team';
import type { TeamRole } from '@/lib/team';
import { TeamMemberCard } from './TeamMemberCard';
import { PendingInviteCard } from './PendingInviteCard';
import { useAlert } from '@/components/AlertDialog';
import { COPY } from '@/lib/commerce/copy';

// ============================================================================
// Types
// ============================================================================

// Founder-status the inviter picks (Task 13). An inviter can only invite a
// co-founder or a team member — never another primary founder (that's
// onboarding-claim territory). The ACCESS `role` is derived from this, not
// chosen independently — see teamRoleForFounderStatus below, which mirrors
// the mapping enforced server-side in supabase/functions/team-invite.
type InviteFounderStatus = 'co_founder' | 'team_member';

function teamRoleForFounderStatus(founderStatus: InviteFounderStatus): TeamRole {
  return founderStatus === 'co_founder' ? 'admin' : 'member';
}

interface TeamManagementProps {
  tier: keyof typeof ADDITIONAL_SEATS_BY_TIER;
  userId: string;
  userName: string;
  userEmail: string;
  // Note: Teams are startup-only (investors are individual users)
}

// ============================================================================
// Component
// ============================================================================

export function TeamManagement({
  tier,
  userId,
  userName,
  userEmail,
}: TeamManagementProps) {
  const colors = useThemeColors();
  const { alert, error, success, confirmDestructive } = useAlert();
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFounderStatus, setInviteFounderStatus] = useState<InviteFounderStatus>('team_member');
  const [isInviting, setIsInviting] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  // Store data
  const team = useTeamStore((s) => s.team);
  const myRole = useMyRole();
  const activeMembers = useActiveMembers();
  const pendingInvites = usePendingInvites();
  const usedSeats = useUsedSeats();

  // Store actions
  const initializeTeam = useTeamStore((s) => s.initializeTeam);
  const inviteMember = useTeamStore((s) => s.inviteMember);
  const removeMember = useTeamStore((s) => s.removeMember);
  const revokeInvite = useTeamStore((s) => s.revokeInvite);
  const canInvite = useTeamStore((s) => s.canInvite);

  // Calculated values
  const totalSeats = getTotalSeats(tier);
  const availableSeats = totalSeats - usedSeats;
  const canManage = myRole ? canManageTeam(myRole) : false;
  const canInviteMore = canInvite(tier);

  // Initialize team if not exists
  React.useEffect(() => {
    if (!team) {
      initializeTeam(userId, userName, userEmail, 'My Startup');
    }
  }, [team, userId, userName, userEmail, initializeTeam]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      error('Invalid Email', 'Please enter a valid email address');
      return;
    }

    setIsInviting(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    const result = await inviteMember(
      inviteEmail.trim().toLowerCase(),
      teamRoleForFounderStatus(inviteFounderStatus),
      userId,
      inviteFounderStatus,
    );

    setIsInviting(false);

    if (result.success) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      setInviteEmail('');
      setShowInviteForm(false);
      success('Invite Sent', `An invitation has been sent to ${inviteEmail}`);
    } else {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      error('Error', result.error || 'Failed to send invite');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    const member = activeMembers.find((m) => m.id === memberId);
    if (!member) return;

    const confirmRemove = async () => {
      if (myRole) {
        const result = await removeMember(memberId, myRole);
        if (result.success) {
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        } else {
          error('Error', result.error || 'Failed to remove member');
        }
      }
    };

    if (await confirmDestructive('Remove Member', `Are you sure you want to remove ${member.name} from the team?`, 'Remove')) {
      confirmRemove();
    }
  };

  const handleRevokeInvite = (inviteId: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    revokeInvite(inviteId);
  };

  // Founder-status options for the invite form (Task 13). Replaces the
  // former Admin/Member selector — access role is now DERIVED from
  // founder-status, not chosen independently by the inviter.
  const founderStatusOptions: { value: InviteFounderStatus; label: string; icon: React.ReactNode }[] = [
    {
      value: 'co_founder',
      label: 'Co-founder',
      icon: <Shield size={16} color={colors.primary[500]} />,
    },
    {
      value: 'team_member',
      label: 'Team member',
      icon: <User size={16} color={colors.text.secondary} />,
    },
  ];

  return (
    <View style={styles.container}>
      {/* Seat Usage Header */}
      <Animated.View entering={FadeIn.duration(400)}>
        <View
          style={[
            styles.seatCard,
            {
              backgroundColor: colors.surface.primary,
              borderColor: colors.border.light,
            },
          ]}
        >
          <View style={styles.seatHeader}>
            <View style={styles.seatHeaderLeft}>
              <Users size={20} color={colors.primary[500]} />
              <Text style={[styles.seatTitle, { color: colors.text.primary }]}>
                Team Seats
              </Text>
            </View>
            <View
              style={[
                styles.seatBadge,
                {
                  backgroundColor:
                    availableSeats > 0
                      ? colors.traffic.greenLight
                      : colors.traffic.redLight,
                },
              ]}
            >
              <Text
                style={[
                  styles.seatBadgeText,
                  {
                    color: availableSeats > 0 ? colors.success : colors.error,
                  },
                ]}
              >
                {usedSeats} / {totalSeats} used
              </Text>
            </View>
          </View>

          {/* Seat progress bar */}
          <View style={[styles.seatProgress, { backgroundColor: colors.surface.secondary }]}>
            <View
              style={[
                styles.seatProgressFill,
                {
                  backgroundColor:
                    usedSeats / totalSeats > 0.9
                      ? colors.error
                      : colors.primary[500],
                  width: `${Math.min(100, (usedSeats / totalSeats) * 100)}%`,
                },
              ]}
            />
          </View>

          {tier === 'free' && (
            <View style={styles.upgradeHint}>
              <AlertCircle size={14} color={colors.text.tertiary} />
              <Text style={[styles.upgradeHintText, { color: colors.text.tertiary }]}>
                {COPY.teamLockedHint}
              </Text>
            </View>
          )}
        </View>
      </Animated.View>

      {/* Invite Button / Form */}
      {canManage && tier !== 'free' && (
        <Animated.View entering={FadeInUp.delay(100).duration(400)}>
          {!showInviteForm ? (
            <AnimatedPressable
              onPress={() => {
                if (canInviteMore) {
                  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  setShowInviteForm(true);
                } else {
                  alert('No Seats Available', 'All team seats are currently in use.');
                }
              }}
              disabled={!canInviteMore}
              style={[
                styles.inviteButton,
                {
                  backgroundColor: canInviteMore
                    ? colors.primary[500]
                    : colors.surface.secondary,
                },
              ]}
            >
              <UserPlus size={18} color={canInviteMore ? '#fff' : colors.text.tertiary} />
              <Text
                style={[
                  styles.inviteButtonText,
                  { color: canInviteMore ? '#fff' : colors.text.tertiary },
                ]}
              >
                Invite Team Member
              </Text>
            </AnimatedPressable>
          ) : (
            <View
              style={[
                styles.inviteForm,
                {
                  backgroundColor: colors.surface.primary,
                  borderColor: colors.border.light,
                },
              ]}
            >
              <Text style={[styles.formTitle, { color: colors.text.primary }]}>
                Invite Team Member
              </Text>

              {/* Email Input */}
              <View style={styles.inputRow}>
                <View
                  style={[
                    styles.inputIcon,
                    { backgroundColor: colors.surface.secondary },
                  ]}
                >
                  <Mail size={18} color={colors.text.tertiary} />
                </View>
                <TextInput
                  style={[
                    styles.input,
                    {
                      backgroundColor: colors.surface.secondary,
                      color: colors.text.primary,
                    },
                  ]}
                  placeholder="Email address"
                  placeholderTextColor={colors.text.tertiary}
                  value={inviteEmail}
                  onChangeText={setInviteEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              {/* Founder-status Selector */}
              <View style={styles.roleSelector}>
                <Text style={[styles.roleLabel, { color: colors.text.secondary }]}>
                  Founder status
                </Text>
                <View style={styles.roleOptions}>
                  {founderStatusOptions.map((option) => (
                    <Pressable
                      key={option.value}
                      onPress={() => setInviteFounderStatus(option.value)}
                      style={[
                        styles.roleOption,
                        {
                          backgroundColor:
                            inviteFounderStatus === option.value
                              ? colors.primary[50]
                              : colors.surface.secondary,
                          borderColor:
                            inviteFounderStatus === option.value
                              ? colors.primary[300]
                              : colors.border.light,
                        },
                      ]}
                    >
                      {option.icon}
                      <Text
                        style={[
                          styles.roleOptionText,
                          {
                            color:
                              inviteFounderStatus === option.value
                                ? colors.primary[600]
                                : colors.text.secondary,
                          },
                        ]}
                      >
                        {option.label}
                      </Text>
                      {inviteFounderStatus === option.value && (
                        <Check size={14} color={colors.primary[500]} />
                      )}
                    </Pressable>
                  ))}
                </View>
              </View>

              {/* Actions */}
              <View style={styles.formActions}>
                <AnimatedPressable
                  onPress={() => {
                    setShowInviteForm(false);
                    setInviteEmail('');
                  }}
                  style={[
                    styles.cancelButton,
                    { backgroundColor: colors.surface.secondary },
                  ]}
                >
                  <Text style={[styles.cancelButtonText, { color: colors.text.secondary }]}>
                    Cancel
                  </Text>
                </AnimatedPressable>
                <AnimatedPressable
                  onPress={handleInvite}
                  disabled={isInviting || !inviteEmail.trim()}
                  style={[
                    styles.sendButton,
                    {
                      backgroundColor:
                        inviteEmail.trim() ? colors.primary[500] : colors.surface.secondary,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.sendButtonText,
                      { color: inviteEmail.trim() ? '#fff' : colors.text.tertiary },
                    ]}
                  >
                    {isInviting ? 'Sending...' : 'Send Invite'}
                  </Text>
                </AnimatedPressable>
              </View>
            </View>
          )}
        </Animated.View>
      )}

      {/* Team Members */}
      {activeMembers.length > 0 && (
        <Animated.View
          entering={FadeInUp.delay(200).duration(400)}
          layout={Layout.springify()}
        >
          <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>
            Team Members
          </Text>
          <View style={styles.memberList}>
            {activeMembers.map((member, index) => (
              <TeamMemberCard
                key={member.id}
                member={member}
                currentUserRole={myRole || 'member'}
                onRemove={canManage ? handleRemoveMember : undefined}
                index={index}
              />
            ))}
          </View>
        </Animated.View>
      )}

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <Animated.View
          entering={FadeInUp.delay(300).duration(400)}
          layout={Layout.springify()}
        >
          <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>
            Pending Invites
          </Text>
          <View style={styles.inviteList}>
            {pendingInvites.map((invite, index) => (
              <PendingInviteCard
                key={invite.id}
                invite={invite}
                onRevoke={canManage ? handleRevokeInvite : undefined}
                index={index}
              />
            ))}
          </View>
        </Animated.View>
      )}
    </View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    gap: 20,
  },
  seatCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    gap: 12,
  },
  seatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  seatHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  seatTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  seatBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  seatBadgeText: {
    fontSize: 13,
    fontWeight: '600',
  },
  seatProgress: {
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  seatProgressFill: {
    height: '100%',
    borderRadius: 3,
  },
  upgradeHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  upgradeHintText: {
    fontSize: 12,
  },
  inviteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
  },
  inviteButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  inviteForm: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    gap: 16,
  },
  formTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inputIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 14,
    fontSize: 15,
  },
  roleSelector: {
    gap: 8,
  },
  roleLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  roleOptions: {
    flexDirection: 'row',
    gap: 10,
  },
  roleOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
  roleOptionText: {
    fontSize: 13,
    fontWeight: '500',
  },
  formActions: {
    flexDirection: 'row',
    gap: 10,
  },
  cancelButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  sendButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
  },
  sendButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 10,
  },
  memberList: {
    gap: 10,
  },
  inviteList: {
    gap: 10,
  },
});
